import React, { useEffect, useMemo, useState } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { auth, db, googleProvider } from "../firebase";
import {
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import { ref, onValue, update, remove, push, set } from "firebase/database";
import { APP_VERSION } from "../version";

/* ---------------- palette ---------------- */
const INK = "#0a0a0a";
const PANEL = "#141310";
const BONE = "#f2ece0";
const ACID = "#D7FF3E";
const DIM = "#a8a293";
const RED = "#FF5A6E";

/* ---------------- helpers ---------------- */
const HE_MONTHS = ["ינואר", "פברואר", "מרץ", "אפריל", "מאי", "יוני", "יולי", "אוגוסט", "ספטמבר", "אוקטובר", "נובמבר", "דצמבר"];
const HE_DAYS = ["א", "ב", "ג", "ד", "ה", "ו", "ש"];
const pad2 = (n) => String(n).padStart(2, "0");
const dateISO = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const parseISO = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };
const monthKeyOf = (iso) => iso.slice(0, 7);
const monthKeyOfDate = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
const hhmm = (d) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
const timeToMin = (t) => { const [h, m] = String(t || "0:0").split(":").map(Number); return (h || 0) * 60 + (m || 0); };
const durMin = (start, end) => { let d = timeToMin(end) - timeToMin(start); if (d < 0) d += 24 * 60; return d; };
const fmtDur = (min) => { const h = Math.floor(min / 60), m = min % 60; if (h && m) return `${h} שע׳ ${m} דק׳`; if (h) return `${h} שע׳`; return `${m} דק׳`; };
const clockDur = (min) => `${Math.floor(min / 60)}:${pad2(min % 60)}`;
const decHours = (min) => { const v = min / 60; return (Math.round(v * 100) / 100).toString(); };
const monthLabel = (d) => `${HE_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
const weekdayOf = (iso) => HE_DAYS[parseISO(iso).getDay()];
const ddmm = (iso) => { const d = parseISO(iso); return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}`; };

function isStandalone() {
  return typeof window !== "undefined" && (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true);
}

/* ---------------- PWA head ---------------- */
function usePwaHead() {
  useEffect(() => {
    const added = [];
    const setMeta = (attr, key, content) => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      const created = !el;
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      const prev = el.getAttribute("content");
      el.setAttribute("content", content);
      added.push({ el, created, prev });
    };
    const link = document.createElement("link");
    link.rel = "manifest"; link.href = "/sigal-hours.webmanifest";
    document.head.appendChild(link);
    const apple = document.createElement("link");
    apple.rel = "apple-touch-icon"; apple.href = "/sigal-hours-icon-180.png";
    document.head.appendChild(apple);
    setMeta("name", "apple-mobile-web-app-capable", "yes");
    setMeta("name", "mobile-web-app-capable", "yes");
    setMeta("name", "apple-mobile-web-app-status-bar-style", "black-translucent");
    setMeta("name", "apple-mobile-web-app-title", "שעות נוספות");
    const themeEl = document.querySelector('meta[name="theme-color"]');
    const prevTheme = themeEl ? themeEl.getAttribute("content") : null;
    if (themeEl) themeEl.setAttribute("content", "#0a0a0a");
    const prevTitle = document.title;
    document.title = "שעות נוספות · סיגל";
    return () => {
      link.remove(); apple.remove();
      added.forEach(({ el, created, prev }) => { if (created) el.remove(); else if (prev != null) el.setAttribute("content", prev); });
      if (themeEl && prevTheme != null) themeEl.setAttribute("content", prevTheme);
      document.title = prevTitle;
    };
  }, []);
}

/* ---------------- entry splash (reveal wave) ---------------- */
function Splash({ onReveal, onDone }) {
  const { cols, rows } = useMemo(() => {
    const vw = typeof window !== "undefined" ? window.innerWidth : 400;
    const vh = typeof window !== "undefined" ? window.innerHeight : 800;
    const c = vw < 600 ? 8 : Math.min(16, Math.max(8, Math.round(vw / 90)));
    return { cols: c, rows: Math.ceil(vh / (vw / c)) + 1 };
  }, []);
  const STEP = 22, DUR = 340, PAUSE = 60, REVEAL = 440;
  const maxDist = cols - 1 + (rows - 1);
  const [gone, setGone] = useState(false);
  useEffect(() => {
    const wipeStart = maxDist * STEP + PAUSE;
    const t1 = setTimeout(() => { setGone(true); onReveal && onReveal(); }, wipeStart);
    const t2 = setTimeout(onDone, 2 * maxDist * STEP + PAUSE + REVEAL + 60);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [maxDist, onReveal, onDone]);
  const cells = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const dist = r + (cols - 1 - c);
      const fwd = dist * STEP;
      const back = maxDist * STEP + PAUSE + (maxDist - dist) * STEP;
      const flash = (r + c) % 2 === 1 ? "#7d9b1e" : ACID;
      cells.push(<SplashCell key={`${r}-${c}`} style={{ "--flash": flash, "--fd": `${DUR}ms`, "--rd": `${REVEAL}ms`, "--f": `${fwd}ms`, "--b": `${back}ms` }} />);
    }
  }
  return (
    <SplashWrap $gone={gone} style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, gridTemplateRows: `repeat(${rows}, 1fr)` }} aria-hidden="true">
      {cells}
    </SplashWrap>
  );
}

/* ---------------- icons ---------------- */
const Svg = (p) => <svg viewBox="0 0 24 24" width={p.size || 22} height={p.size || 22} fill="none" stroke="currentColor" strokeWidth={p.sw || 2} strokeLinecap="round" strokeLinejoin="round">{p.children}</svg>;
const IcPlay = (p) => <svg viewBox="0 0 24 24" width={p.size || 44} height={p.size || 44} fill="currentColor"><path d="M8 5v14l11-7z" /></svg>;
const IcStop = (p) => <svg viewBox="0 0 24 24" width={p.size || 40} height={p.size || 40} fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2" /></svg>;
const IcPlus = (p) => <Svg size={p.size} sw={2.4}><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></Svg>;
const IcTrash = (p) => <Svg size={p.size || 18}><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></Svg>;
const IcChevR = (p) => <Svg size={p.size || 20}><polyline points="15 18 9 12 15 6" /></Svg>;
const IcChevL = (p) => <Svg size={p.size || 20}><polyline points="9 18 15 12 9 6" /></Svg>;
const IcDoc = (p) => <Svg size={p.size || 18}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="9" y1="15" x2="15" y2="15" /></Svg>;
const IcGear = (p) => <Svg size={p.size || 20}><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></Svg>;
const IcWhats = (p) => <svg viewBox="0 0 24 24" width={p.size || 18} height={p.size || 18} fill="currentColor"><path d="M17.5 14.4c-.3-.15-1.8-.9-2-1s-.5-.15-.7.15-.8 1-.95 1.2-.35.22-.65.07a8.2 8.2 0 0 1-2.4-1.48 9 9 0 0 1-1.66-2.07c-.17-.3 0-.46.13-.6l.45-.53c.15-.17.2-.3.3-.5s.05-.37 0-.52-.7-1.67-.95-2.29c-.25-.6-.5-.52-.7-.53h-.6a1.1 1.1 0 0 0-.8.38 3.36 3.36 0 0 0-1.05 2.5 5.85 5.85 0 0 0 1.22 3.1c.15.2 2.1 3.2 5.1 4.49 2.98 1.3 2.98.87 3.52.82.54-.05 1.75-.72 2-1.4.24-.7.24-1.28.17-1.4-.07-.13-.27-.2-.57-.35zM12 2a10 10 0 0 0-8.6 15.06L2 22l5.06-1.33A10 10 0 1 0 12 2z" /></svg>;
const IcLogout = (p) => <Svg size={p.size || 18}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></Svg>;
const IcRefresh = (p) => <Svg size={p.size || 17}><path d="M21 12a9 9 0 1 1-3-6.7" /><polyline points="21 3 21 9 15 9" /></Svg>;
const IcClose = (p) => <Svg size={p.size || 20}><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></Svg>;

/* =====================================================================
   Component
===================================================================== */
export const SigalHours = () => {
  usePwaHead();
  const [authUser, setAuthUser] = useState(undefined);
  const [authBusy, setAuthBusy] = useState(false);
  const [data, setData] = useState(null);
  const [monthCursor, setMonthCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [nowTick, setNowTick] = useState(Date.now());
  const [showSplash, setShowSplash] = useState(true);
  const [contentReady, setContentReady] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editDate, setEditDate] = useState(null); // ISO string or null
  const [reportMonth, setReportMonth] = useState(null); // Date (1st) or null
  const [allOpen, setAllOpen] = useState(false);
  const [printJob, setPrintJob] = useState(null); // {type:'month'|'all', month?:Date}
  const [avatarErr, setAvatarErr] = useState(false);

  const uid = authUser && authUser.uid;
  const base = uid ? `sigalHours/users/${uid}` : null;
  const userName = (authUser && (authUser.displayName || (authUser.email || "").split("@")[0])) || "";

  useEffect(() => onAuthStateChanged(auth, (u) => setAuthUser(u || null)), []);
  useEffect(() => { getRedirectResult(auth).catch(() => {}); }, []);
  useEffect(() => {
    if (!base) { setData(null); return; }
    return onValue(ref(db, base), (snap) => setData(snap.val() || {}));
  }, [base]);

  const running = data && data.running ? data.running : null;
  // live tick only while running
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [running]);
  // safety: always reveal content
  useEffect(() => { const t = setTimeout(() => setContentReady(true), 4500); return () => clearTimeout(t); }, []);

  /* ---- auth ---- */
  const signIn = async () => {
    setAuthBusy(true);
    try {
      if (isStandalone()) { await signInWithRedirect(auth, googleProvider); return; }
      await signInWithPopup(auth, googleProvider);
    } catch {
      try { await signInWithRedirect(auth, googleProvider); return; } catch { alert("ההתחברות נכשלה. נסו שוב."); }
    } finally { setAuthBusy(false); }
  };
  const signOutNow = () => signOut(auth).catch(() => {});
  const doRefresh = async () => {
    try {
      if ("serviceWorker" in navigator) { const regs = await navigator.serviceWorker.getRegistrations(); await Promise.all(regs.map((r) => r.update().catch(() => {}))); }
      if (window.caches) { const keys = await caches.keys(); await Promise.all(keys.map((k) => caches.delete(k))); }
    } catch {}
    window.location.reload();
  };

  /* ---- start / stop ---- */
  const toggleTimer = () => {
    if (!base) return;
    if (running) {
      const startD = new Date(running.start);
      const endD = new Date();
      const iso = running.day || dateISO(startD);
      const entry = { start: hhmm(startD), end: hhmm(endD), ts: Date.now() };
      push(ref(db, `${base}/entries/${iso}`), entry);
      set(ref(db, `${base}/running`), null);
    } else {
      set(ref(db, `${base}/running`), { start: new Date().toISOString(), day: dateISO(new Date()) });
    }
  };

  /* ---- entry CRUD ---- */
  const addEntry = (iso, start, end, note) => {
    if (!base) return;
    const e = { start, end, ts: Date.now() };
    if (note && note.trim()) e.note = note.trim();
    push(ref(db, `${base}/entries/${iso}`), e);
  };
  const deleteEntry = (iso, id) => { if (base) remove(ref(db, `${base}/entries/${iso}/${id}`)); };
  const updateEntry = (iso, id, start, end, note) => {
    if (!base) return;
    update(ref(db, `${base}/entries/${iso}/${id}`), { start, end, note: note && note.trim() ? note.trim() : null });
  };
  const deleteMonth = (mKey) => {
    if (!base || !data || !data.entries) return;
    const ups = {};
    Object.keys(data.entries).forEach((iso) => { if (monthKeyOf(iso) === mKey) ups[`entries/${iso}`] = null; });
    if (Object.keys(ups).length) update(ref(db, base), ups);
  };

  /* ---- derive month rows ---- */
  const entriesByDay = (mKeyDate) => {
    const mKey = monthKeyOfDate(mKeyDate);
    const out = [];
    const all = (data && data.entries) || {};
    Object.keys(all).filter((iso) => monthKeyOf(iso) === mKey).sort().forEach((iso) => {
      const day = all[iso] || {};
      const items = Object.keys(day).map((id) => ({ id, ...day[id], min: durMin(day[id].start, day[id].end) }))
        .sort((a, b) => timeToMin(a.start) - timeToMin(b.start));
      const total = items.reduce((s, it) => s + it.min, 0);
      if (items.length) out.push({ iso, items, total });
    });
    return out;
  };
  const monthDays = useMemo(() => entriesByDay(monthCursor), [data, monthCursor]); // eslint-disable-line
  const monthTotal = monthDays.reduce((s, d) => s + d.total, 0);

  const allMonths = useMemo(() => {
    const all = (data && data.entries) || {};
    const map = {};
    Object.keys(all).forEach((iso) => {
      const mk = monthKeyOf(iso);
      const day = all[iso] || {};
      Object.keys(day).forEach((id) => { map[mk] = (map[mk] || 0) + durMin(day[id].start, day[id].end); });
    });
    return Object.keys(map).sort().reverse().map((mk) => ({ mk, min: map[mk] }));
  }, [data]);
  const grandTotal = allMonths.reduce((s, m) => s + m.min, 0);

  /* ---- print ---- */
  useEffect(() => {
    if (!printJob) return;
    const t = setTimeout(() => {
      const after = () => { setPrintJob(null); window.removeEventListener("afterprint", after); };
      window.addEventListener("afterprint", after);
      try { window.print(); } catch { setPrintJob(null); }
      // fallback clear (some browsers never fire afterprint)
      setTimeout(() => setPrintJob((p) => (p === printJob ? null : p)), 800);
    }, 120);
    return () => clearTimeout(t);
  }, [printJob]);

  /* ---- whatsapp ---- */
  const shareWhatsAppMonth = (mDate) => {
    const days = entriesByDay(mDate);
    const total = days.reduce((s, d) => s + d.total, 0);
    let txt = `שעות נוספות — ${userName}\n${monthLabel(mDate)}\n\n`;
    days.forEach((d) => {
      txt += `${ddmm(d.iso)} (${weekdayOf(d.iso)}׳): ` + d.items.map((it) => `${it.start}–${it.end}`).join(", ") + ` · ${clockDur(d.total)}\n`;
    });
    if (!days.length) txt += "אין דיווחים החודש.\n";
    txt += `\nסה״כ: ${fmtDur(total)} (${decHours(total)} שע׳)`;
    openWhats(txt);
  };
  const shareWhatsAppAll = () => {
    let txt = `שעות נוספות — ${userName}\nסיכום כולל\n\n`;
    allMonths.forEach((m) => { const [y, mm] = m.mk.split("-"); txt += `${HE_MONTHS[Number(mm) - 1]} ${y}: ${fmtDur(m.min)} (${decHours(m.min)} שע׳)\n`; });
    if (!allMonths.length) txt += "אין דיווחים.\n";
    txt += `\nסה״כ כולל: ${fmtDur(grandTotal)} (${decHours(grandTotal)} שע׳)`;
    openWhats(txt);
  };
  const openWhats = (txt) => {
    const url = "https://wa.me/?text=" + encodeURIComponent(txt);
    try { const w = window.open(url, "_blank"); if (!w) window.location.href = url; } catch { window.location.href = url; }
  };

  const splashEl = showSplash ? <Splash onReveal={() => setContentReady(true)} onDone={() => setShowSplash(false)} /> : null;

  /* ---- loading / gate ---- */
  if (authUser === undefined) {
    return (<Shell dir="rtl"><Global />{splashEl}</Shell>);
  }
  if (!authUser) {
    return (
      <Shell dir="rtl">
        <Global />{splashEl}
        <Gate>
          <GateTag>דיווח · פרטי · למסך הבית</GateTag>
          <GateTitle>שעות<br />נוספות</GateTitle>
          <GateSub>מעקב שעות נוספות — התחלה/סיום בלחיצה, עריכה לפי יום, ודוחות חודשיים.<br />התחברי עם Google כדי להתחיל.</GateSub>
          <GBtn onClick={signIn} disabled={authBusy}><GG>G</GG>{authBusy ? "מתחבר…" : "כניסה עם Google"}</GBtn>
          <GateFoot>כפיר מילר ©</GateFoot>
        </Gate>
      </Shell>
    );
  }

  const elapsedMs = running ? Math.max(0, nowTick - new Date(running.start).getTime()) : 0;
  const elapsedS = Math.floor(elapsedMs / 1000);
  const fmtElapsed = `${pad2(Math.floor(elapsedS / 3600))}:${pad2(Math.floor((elapsedS % 3600) / 60))}:${pad2(elapsedS % 60)}`;
  const thisMonthKey = monthKeyOfDate(new Date());
  const viewingCurrent = monthKeyOfDate(monthCursor) === thisMonthKey;

  return (
    <Shell dir="rtl">
      <Global />
      {splashEl}
      <Main $show={contentReady} data-app>
        {/* header */}
        <Head>
          <HeadTop>
            <Title>שעות נוספות</Title>
            <GearWrap>
              <IconBtn onClick={() => setMenuOpen((o) => !o)} title="תפריט" aria-label="תפריט"><IcGear /></IconBtn>
              {menuOpen && (
                <>
                  <Backdrop onClick={() => setMenuOpen(false)} />
                  <Menu>
                    <MenuName>{authUser.displayName || authUser.email}</MenuName>
                    <MenuItem onClick={() => { setMenuOpen(false); setAllOpen(true); }}><span>כל הדוחות</span><IcDoc /></MenuItem>
                    <MenuItem onClick={() => { setMenuOpen(false); signOutNow(); }}><span>התנתקות</span><IcLogout /></MenuItem>
                    <MenuFoot>
                      <MenuVer>גרסה {APP_VERSION}</MenuVer>
                      <RefreshBtn onClick={() => { setMenuOpen(false); doRefresh(); }} title="רענון" aria-label="רענון"><IcRefresh /></RefreshBtn>
                    </MenuFoot>
                  </Menu>
                </>
              )}
            </GearWrap>
          </HeadTop>
          <Greet>
            {authUser.photoURL && !avatarErr && (
              <Ava src={authUser.photoURL} alt="" referrerPolicy="no-referrer" onError={() => setAvatarErr(true)} />
            )}
            <GreetTxt>שלום {userName.split(" ")[0] || userName}</GreetTxt>
          </Greet>
        </Head>

        {/* month nav */}
        <MonthNav>
          <NavBtn onClick={() => setMonthCursor((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))} aria-label="חודש קודם"><IcChevL /></NavBtn>
          <MonthLbl onClick={() => setMonthCursor(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); })} title="חזרה לחודש הנוכחי">
            {monthLabel(monthCursor)}{!viewingCurrent && <MonthNow>היום</MonthNow>}
          </MonthLbl>
          <NavBtn onClick={() => setMonthCursor((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))} aria-label="חודש הבא"><IcChevR /></NavBtn>
        </MonthNav>

        {/* start/stop */}
        <TimerZone>
          <BigBtn $running={!!running} onClick={toggleTimer} aria-label={running ? "סיום" : "התחל"}>
            <BtnInner key={running ? "run" : "idle"}>
              {running ? <IcStop /> : <IcPlay />}
              <BtnLabel>{running ? "סיום" : "התחל"}</BtnLabel>
            </BtnInner>
            {running && <Ring />}
          </BigBtn>
          {running ? (
            <>
              <LiveClock>{fmtElapsed}</LiveClock>
              <RunHint>מתעד מהשעה {hhmm(new Date(running.start))}</RunHint>
            </>
          ) : (
            <IdleHint>לחצי כדי להתחיל לתעד שעות נוספות</IdleHint>
          )}
        </TimerZone>

        {/* month total */}
        <TotalCard>
          <TotalLbl>סה״כ החודש</TotalLbl>
          <TotalVal>{clockDur(monthTotal)}<TotalUnit>שעות</TotalUnit></TotalVal>
          <TotalDays>{monthDays.length ? `${monthDays.length} ימים · ${decHours(monthTotal)} שע׳` : "אין דיווחים עדיין"}</TotalDays>
        </TotalCard>

        {/* actions */}
        <ActionsRow>
          <ActBtn onClick={() => setEditDate(dateISO(new Date()))}><IcPlus size={18} />הוסף / ערוך יום</ActBtn>
          <ActBtn onClick={() => setReportMonth(new Date(monthCursor))} disabled={!monthDays.length}><IcDoc />דוח חודשי</ActBtn>
        </ActionsRow>

        {/* day list */}
        <DayList>
          {monthDays.length === 0 && <Empty>אין שעות נוספות ב{HE_MONTHS[monthCursor.getMonth()]}.<br />לחצי «התחל» או «הוסף יום».</Empty>}
          {monthDays.map((d) => (
            <DayCard key={d.iso} onClick={() => setEditDate(d.iso)}>
              <DayHead>
                <DayDate><DayNum>{parseISO(d.iso).getDate()}</DayNum><DayWk>{weekdayOf(d.iso)}׳</DayWk></DayDate>
                <DayTotal>{clockDur(d.total)}</DayTotal>
              </DayHead>
              <DayEntries>
                {d.items.map((it) => (
                  <EntryPill key={it.id}>
                    <EntryTime>{it.start}–{it.end}</EntryTime>
                    <EntryDur>{fmtDur(it.min)}</EntryDur>
                    {it.note && <EntryNote>· {it.note}</EntryNote>}
                  </EntryPill>
                ))}
              </DayEntries>
            </DayCard>
          ))}
        </DayList>

        <Foot>גרסה · שעות נוספות · כפיר מילר ©</Foot>
      </Main>

      {/* day editor */}
      {editDate && (
        <DayEditor
          iso={editDate}
          data={data}
          onChangeDate={setEditDate}
          onAdd={addEntry}
          onUpdate={updateEntry}
          onDelete={deleteEntry}
          onClose={() => setEditDate(null)}
        />
      )}

      {/* month report */}
      {reportMonth && (
        <ReportModal
          title={`דוח חודשי — ${monthLabel(reportMonth)}`}
          days={entriesByDay(reportMonth)}
          total={entriesByDay(reportMonth).reduce((s, d) => s + d.total, 0)}
          onClose={() => setReportMonth(null)}
          onPdf={() => setPrintJob({ type: "month", month: new Date(reportMonth) })}
          onWhats={() => shareWhatsAppMonth(reportMonth)}
        />
      )}

      {/* all reports */}
      {allOpen && (
        <AllReports
          months={allMonths}
          grand={grandTotal}
          onOpenMonth={(mk) => { const [y, m] = mk.split("-").map(Number); setMonthCursor(new Date(y, m - 1, 1)); setReportMonth(new Date(y, m - 1, 1)); setAllOpen(false); }}
          onDeleteMonth={deleteMonth}
          onPdf={() => setPrintJob({ type: "all" })}
          onWhats={shareWhatsAppAll}
          onClose={() => setAllOpen(false)}
        />
      )}

      {/* print document */}
      {printJob && (
        <PrintRoot className="print-root" dir="rtl">
          {printJob.type === "month" ? (
            <PrintMonth mDate={printJob.month} days={entriesByDay(printJob.month)} name={userName} />
          ) : (
            <PrintAll months={allMonths} grand={grandTotal} name={userName} />
          )}
        </PrintRoot>
      )}
    </Shell>
  );
};

/* 24-hour time picker (two selects) — no AM/PM, consistent on every device */
function Time24({ value, onChange }) {
  const [h, m] = value && value.includes(":") ? value.split(":") : ["", ""];
  const set = (nh, nm) => onChange(`${nh || "00"}:${nm || "00"}`);
  const HOURS = Array.from({ length: 24 }, (_, i) => pad2(i));
  const MINS = Array.from({ length: 60 }, (_, i) => pad2(i));
  return (
    <T24Wrap>
      <T24Sel value={h} onChange={(e) => set(e.target.value, m)} aria-label="שעה">
        <option value="" disabled>--</option>
        {HOURS.map((hh) => <option key={hh} value={hh}>{hh}</option>)}
      </T24Sel>
      <T24Colon>:</T24Colon>
      <T24Sel value={m} onChange={(e) => set(h, e.target.value)} aria-label="דקה">
        <option value="" disabled>--</option>
        {MINS.map((mm) => <option key={mm} value={mm}>{mm}</option>)}
      </T24Sel>
    </T24Wrap>
  );
}

/* =====================================================================
   Day editor modal
===================================================================== */
function DayEditor({ iso, data, onChangeDate, onAdd, onUpdate, onDelete, onClose }) {
  const day = (data && data.entries && data.entries[iso]) || {};
  const items = Object.keys(day).map((id) => ({ id, ...day[id], min: durMin(day[id].start, day[id].end) })).sort((a, b) => timeToMin(a.start) - timeToMin(b.start));
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [note, setNote] = useState("");
  const [editingId, setEditingId] = useState(null);
  const total = items.reduce((s, it) => s + it.min, 0);
  const canAdd = start && end;
  const resetForm = () => { setStart(""); setEnd(""); setNote(""); setEditingId(null); };
  const beginEdit = (it) => { setEditingId(it.id); setStart(it.start); setEnd(it.end); setNote(it.note || ""); };
  const submit = () => {
    if (!canAdd) return;
    if (editingId) onUpdate(iso, editingId, start, end, note);
    else onAdd(iso, start, end, note);
    resetForm();
  };
  return (
    <Overlay onClick={onClose}>
      <Sheet onClick={(e) => e.stopPropagation()}>
        <SheetHead>
          <SheetTitle>עריכת יום</SheetTitle>
          <IconBtn onClick={onClose} aria-label="סגור"><IcClose /></IconBtn>
        </SheetHead>

        <FieldLbl>תאריך</FieldLbl>
        <DateInput type="date" value={iso} onChange={(e) => e.target.value && onChangeDate(e.target.value)} />

        <FieldLbl>שעות שדווחו {items.length ? `· סה״כ ${clockDur(total)}` : ""}</FieldLbl>
        {items.length === 0 && <NoneYet>עדיין לא דווחו שעות ליום זה.</NoneYet>}
        {items.map((it) => (
          <EditRow key={it.id} $active={editingId === it.id} onClick={() => beginEdit(it)}>
            <EditTimes>{it.start}–{it.end}</EditTimes>
            <EditDur>{fmtDur(it.min)}</EditDur>
            {it.note && <EditNote>{it.note}</EditNote>}
            <EditHint>הקישי לעריכה</EditHint>
            <DelBtn onClick={(e) => { e.stopPropagation(); if (editingId === it.id) resetForm(); onDelete(iso, it.id); }} aria-label="מחק"><IcTrash /></DelBtn>
          </EditRow>
        ))}

        <FieldLbl>{editingId ? "עריכת שעות" : "הוספת שעות"}</FieldLbl>
        <AddGrid>
          <TimeCol>
            <TimeMini>מהשעה</TimeMini>
            <Time24 value={start} onChange={setStart} />
          </TimeCol>
          <TimeCol>
            <TimeMini>עד השעה</TimeMini>
            <Time24 value={end} onChange={setEnd} />
          </TimeCol>
        </AddGrid>
        <NoteInput placeholder="הערה (לא חובה)" value={note} onChange={(e) => setNote(e.target.value)} />
        <AddBtn onClick={submit} disabled={!canAdd}>{editingId ? <>שמירת שינויים</> : <><IcPlus size={18} />הוסף</>}</AddBtn>
        {editingId && <CancelBtn onClick={resetForm}>ביטול עריכה</CancelBtn>}
      </Sheet>
    </Overlay>
  );
}

/* =====================================================================
   Month report modal (preview)
===================================================================== */
function ReportModal({ title, days, total, onClose, onPdf, onWhats }) {
  return (
    <Overlay onClick={onClose}>
      <Sheet onClick={(e) => e.stopPropagation()}>
        <SheetHead>
          <SheetTitle>{title}</SheetTitle>
          <IconBtn onClick={onClose} aria-label="סגור"><IcClose /></IconBtn>
        </SheetHead>
        <RepBig>{clockDur(total)}<RepUnit>שעות · {decHours(total)} שע׳</RepUnit></RepBig>
        <RepTable>
          <RepHeadRow><RepTh>תאריך</RepTh><RepTh>שעות</RepTh><RepTh>סה״כ</RepTh></RepHeadRow>
          {days.map((d) => (
            <RepRow key={d.iso}>
              <RepTd>{ddmm(d.iso)} ({weekdayOf(d.iso)}׳)</RepTd>
              <RepTd>{d.items.map((it) => `${it.start}–${it.end}`).join(", ")}</RepTd>
              <RepTd $b>{clockDur(d.total)}</RepTd>
            </RepRow>
          ))}
          {!days.length && <RepRow><RepTd $span={3} style={{ color: DIM }}>אין דיווחים.</RepTd></RepRow>}
        </RepTable>
        <SheetActions>
          <PriBtn onClick={onPdf}><IcDoc />שמירה כ-PDF</PriBtn>
          <WhatsBtn onClick={onWhats}><IcWhats />שליחה ב-WhatsApp</WhatsBtn>
        </SheetActions>
      </Sheet>
    </Overlay>
  );
}

/* =====================================================================
   All reports modal
===================================================================== */
function AllReports({ months, grand, onOpenMonth, onDeleteMonth, onPdf, onWhats, onClose }) {
  const [confirmMk, setConfirmMk] = useState(null);
  return (
    <Overlay onClick={onClose}>
      <Sheet onClick={(e) => e.stopPropagation()}>
        <SheetHead>
          <SheetTitle>כל הדוחות</SheetTitle>
          <IconBtn onClick={onClose} aria-label="סגור"><IcClose /></IconBtn>
        </SheetHead>
        <RepBig>{clockDur(grand)}<RepUnit>סה״כ כולל · {decHours(grand)} שע׳</RepUnit></RepBig>
        <MonthsList>
          {months.map((m) => {
            const [y, mm] = m.mk.split("-");
            return (
              <MonthRow key={m.mk}>
                <MonthRowMain onClick={() => onOpenMonth(m.mk)}>
                  <MonthRowName>{HE_MONTHS[Number(mm) - 1]} {y}</MonthRowName>
                  <MonthRowVal>{clockDur(m.min)} <small>({decHours(m.min)} שע׳)</small></MonthRowVal>
                </MonthRowMain>
                {confirmMk === m.mk ? (
                  <ConfirmDel>
                    <ConfirmTxt>למחוק?</ConfirmTxt>
                    <ConfirmYes onClick={() => { onDeleteMonth(m.mk); setConfirmMk(null); }}>כן</ConfirmYes>
                    <ConfirmNo onClick={() => setConfirmMk(null)}>לא</ConfirmNo>
                  </ConfirmDel>
                ) : (
                  <DelBtn onClick={() => setConfirmMk(m.mk)} aria-label="מחיקת חודש"><IcTrash /></DelBtn>
                )}
              </MonthRow>
            );
          })}
          {!months.length && <NoneYet>אין דיווחים עדיין.</NoneYet>}
        </MonthsList>
        <SheetActions>
          <PriBtn onClick={onPdf} disabled={!months.length}><IcDoc />סיכום PDF</PriBtn>
          <WhatsBtn onClick={onWhats} disabled={!months.length}><IcWhats />סיכום ב-WhatsApp</WhatsBtn>
        </SheetActions>
      </Sheet>
    </Overlay>
  );
}

/* =====================================================================
   Print documents (white, RTL, print-only)
===================================================================== */
function PrintMonth({ mDate, days, name }) {
  const total = days.reduce((s, d) => s + d.total, 0);
  return (
    <>
      <PDocHead>
        <PDocTitle>דוח שעות נוספות</PDocTitle>
        <PDocName>{name} · {monthLabel(mDate)}</PDocName>
      </PDocHead>
      <PTable>
        <thead><tr><PTh>תאריך</PTh><PTh>יום</PTh><PTh>שעות</PTh><PTh>סה״כ</PTh></tr></thead>
        <tbody>
          {days.map((d) => (
            <tr key={d.iso}>
              <PTd>{ddmm(d.iso)}</PTd>
              <PTd>{weekdayOf(d.iso)}׳</PTd>
              <PTd>{d.items.map((it) => `${it.start}–${it.end}`).join(", ")}</PTd>
              <PTd $b>{clockDur(d.total)}</PTd>
            </tr>
          ))}
        </tbody>
        <tfoot><tr><PTd colSpan={3} $b>סה״כ</PTd><PTd $b>{clockDur(total)} ({decHours(total)} שע׳)</PTd></tr></tfoot>
      </PTable>
      <PSign>חתימה: ____________________</PSign>
    </>
  );
}
function PrintAll({ months, grand, name }) {
  return (
    <>
      <PDocHead>
        <PDocTitle>דוח שעות נוספות — סיכום כולל</PDocTitle>
        <PDocName>{name}</PDocName>
      </PDocHead>
      <PTable>
        <thead><tr><PTh>חודש</PTh><PTh>סה״כ שעות</PTh></tr></thead>
        <tbody>
          {months.map((m) => { const [y, mm] = m.mk.split("-"); return (
            <tr key={m.mk}><PTd>{HE_MONTHS[Number(mm) - 1]} {y}</PTd><PTd $b>{clockDur(m.min)} ({decHours(m.min)} שע׳)</PTd></tr>
          ); })}
        </tbody>
        <tfoot><tr><PTd $b>סה״כ כולל</PTd><PTd $b>{clockDur(grand)} ({decHours(grand)} שע׳)</PTd></tr></tfoot>
      </PTable>
    </>
  );
}

/* =====================================================================
   styles
===================================================================== */
const fade = keyframes`from{opacity:0}to{opacity:1}`;
const cellFlash = keyframes`0%{background-color:${INK}}45%{background-color:var(--flash)}100%{background-color:${INK}}`;
const cellReveal = keyframes`0%{background-color:${INK};opacity:1}35%{background-color:var(--flash);opacity:1}55%{background-color:var(--flash);opacity:1}100%{background-color:var(--flash);opacity:0}`;
const btnPop = keyframes`0%{transform:scale(.6) rotateY(90deg);opacity:0}60%{transform:scale(1.08) rotateY(0);opacity:1}100%{transform:scale(1);opacity:1}`;
const ringPulse = keyframes`0%{transform:scale(1);opacity:.7}100%{transform:scale(1.35);opacity:0}`;

const Global = createGlobalStyle`
  html, body, #root { height: 100%; }
  body { background: ${INK}; margin: 0; }
  #root * { box-sizing: border-box; }
  * { -webkit-tap-highlight-color: transparent; }
  @media print {
    body * { visibility: hidden !important; }
    .print-root, .print-root * { visibility: visible !important; }
    .print-root { position: absolute; inset: 0; }
  }
`;
const Shell = styled.div`
  min-height: 100%;
  background:
    radial-gradient(120% 60% at 100% -10%, rgba(215,255,62,.08), transparent 55%),
    radial-gradient(120% 60% at 0% 0%, rgba(44,107,255,.07), transparent 55%),
    ${INK};
  color: ${BONE};
  font-family: "Heebo", "Assistant", system-ui, sans-serif;
  padding: env(safe-area-inset-top) 0 env(safe-area-inset-bottom);
`;
const SplashWrap = styled.div`
  position: fixed; inset: 0; z-index: 9999; display: grid; gap: 0; overflow: hidden;
  background: transparent; pointer-events: ${(p) => (p.$gone ? "none" : "auto")};
`;
const SplashCell = styled.span`
  opacity: 1; background-color: ${INK};
  animation-name: ${cellFlash}, ${cellReveal};
  animation-timing-function: ease, cubic-bezier(.4,0,.2,1);
  animation-fill-mode: both, forwards;
  animation-duration: var(--fd), var(--rd);
  animation-delay: var(--f), var(--b);
`;
const Main = styled.div`
  max-width: 620px; margin: 0 auto; padding: 0 16px 40px;
  opacity: ${(p) => (p.$show ? 1 : 0)}; transform: ${(p) => (p.$show ? "none" : "translateY(12px)")};
  transition: opacity .35s ease, transform .35s cubic-bezier(.22,1,.36,1);
`;

/* gate */
const Gate = styled.div`min-height: 100vh; display:flex; flex-direction:column; justify-content:center; padding: 40px 26px; max-width:560px; margin:0 auto;`;
const GateTag = styled.div`font-size:12px; font-weight:800; letter-spacing:3px; color:${ACID}; margin-bottom:18px;`;
const GateTitle = styled.h1`margin:0; font-weight:900; line-height:.86; font-size:clamp(70px,26vw,150px); letter-spacing:-3px; color:${BONE};`;
const GateSub = styled.p`font-size:17px; font-weight:600; line-height:1.7; color:${DIM}; margin:24px 0 32px; max-width:34ch;`;
const GBtn = styled.button`align-self:flex-start; display:inline-flex; align-items:center; gap:12px; border:none; cursor:pointer; font-family:inherit; font-size:18px; font-weight:900; color:${INK}; background:${ACID}; padding:16px 34px; transition:transform .08s,filter .15s; &:hover:not(:disabled){filter:brightness(1.05)} &:active{transform:translateY(2px)} &:disabled{opacity:.6}`;
const GG = styled.span`font-family:Arial,sans-serif; font-weight:900; font-size:20px; color:${INK};`;
const GateFoot = styled.div`margin-top:44px; font-size:12px; font-weight:700; letter-spacing:2px; color:#5a564d;`;

/* header */
const Head = styled.header`padding: 22px 2px 6px;`;
const HeadTop = styled.div`display:flex; align-items:center; justify-content:space-between;`;
const Title = styled.h1`margin:0; font-weight:900; font-size:clamp(34px,9vw,48px); letter-spacing:-1.5px; color:${BONE};`;
const Greet = styled.div`display:flex; align-items:center; gap:10px; margin-top:8px;`;
const Ava = styled.img`width:34px; height:34px; border-radius:50%; object-fit:cover; border:1px solid #2a2822; flex:none;`;
const GreetTxt = styled.div`font-weight:800; color:${ACID}; font-size:16px;`;
const GearWrap = styled.div`position:relative;`;
const IconBtn = styled.button`display:grid; place-items:center; width:42px; height:42px; border:1px solid #2a2822; background:${PANEL}; color:${BONE}; cursor:pointer; border-radius:0; &:active{transform:translateY(1px)}`;
const Backdrop = styled.div`position:fixed; inset:0; z-index:40;`;
const Menu = styled.div`position:absolute; top:48px; left:0; z-index:50; min-width:210px; background:${PANEL}; border:1px solid #2a2822; box-shadow:0 20px 50px rgba(0,0,0,.5);`;
const MenuName = styled.div`padding:12px 14px; font-size:12px; font-weight:700; color:${DIM}; border-bottom:1px solid #211f1a; direction:ltr; text-align:right; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;`;
const MenuItem = styled.button`width:100%; display:flex; align-items:center; justify-content:space-between; gap:10px; padding:13px 14px; background:none; border:none; border-bottom:1px solid #211f1a; color:${BONE}; font-family:inherit; font-size:15px; font-weight:700; cursor:pointer; &:hover{background:#1c1a16}`;
const MenuFoot = styled.div`display:flex; align-items:center; justify-content:space-between; padding:10px 14px;`;
const MenuVer = styled.span`font-size:12px; font-weight:700; letter-spacing:1px; color:#5a564d;`;
const RefreshBtn = styled.button`display:grid; place-items:center; width:34px; height:34px; background:none; border:1px solid #2a2822; color:${BONE}; cursor:pointer; &:active{transform:translateY(1px)} &:hover{color:${ACID}}`;

/* month nav */
const MonthNav = styled.div`display:flex; align-items:center; justify-content:space-between; margin:18px 0 14px; background:${PANEL}; border:1px solid #2a2822;`;
const NavBtn = styled.button`display:grid; place-items:center; width:52px; height:48px; background:none; border:none; color:${BONE}; cursor:pointer; &:active{color:${ACID}}`;
const MonthLbl = styled.button`flex:1; background:none; border:none; color:${BONE}; font-family:inherit; font-weight:900; font-size:19px; cursor:pointer; display:flex; align-items:center; justify-content:center; gap:8px;`;
const MonthNow = styled.span`font-size:11px; font-weight:800; color:${INK}; background:${ACID}; padding:2px 7px;`;

/* timer */
const TimerZone = styled.div`display:flex; flex-direction:column; align-items:center; padding:16px 0 6px;`;
const BigBtn = styled.button`
  position:relative; width:190px; height:190px; border-radius:50%; border:none; cursor:pointer;
  background:${(p) => (p.$running ? RED : ACID)}; color:${INK};
  box-shadow:0 14px 40px ${(p) => (p.$running ? "rgba(255,90,110,.35)" : "rgba(215,255,62,.30)")};
  transition:background-color .35s ease, box-shadow .35s ease;
  &:active{transform:translateY(2px)}
`;
const BtnInner = styled.span`display:flex; flex-direction:column; align-items:center; gap:4px; animation:${btnPop} .4s cubic-bezier(.22,1.4,.4,1) both;`;
const BtnLabel = styled.span`font-weight:900; font-size:22px; letter-spacing:-.5px;`;
const Ring = styled.span`position:absolute; inset:0; border-radius:50%; border:3px solid ${RED}; animation:${ringPulse} 1.6s ease-out infinite;`;
const LiveClock = styled.div`margin-top:22px; font-weight:900; font-size:44px; letter-spacing:1px; color:${BONE}; font-variant-numeric:tabular-nums;`;
const RunHint = styled.div`margin-top:2px; font-weight:700; color:${RED}; font-size:14px;`;
const IdleHint = styled.div`margin-top:20px; font-weight:600; color:${DIM}; font-size:15px; text-align:center;`;

/* total card */
const TotalCard = styled.div`margin-top:20px; background:${PANEL}; border:1px solid #2a2822; padding:18px 20px; display:flex; flex-direction:column; gap:2px;`;
const TotalLbl = styled.div`font-weight:800; color:${DIM}; font-size:13px; letter-spacing:1px;`;
const TotalVal = styled.div`font-weight:900; font-size:40px; color:${ACID}; display:flex; align-items:baseline; gap:8px; line-height:1.05;`;
const TotalUnit = styled.span`font-size:16px; color:${BONE}; font-weight:700;`;
const TotalDays = styled.div`font-size:14px; color:${DIM}; font-weight:700; margin-top:4px;`;

/* actions */
const ActionsRow = styled.div`display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-top:14px;`;
const ActBtn = styled.button`display:flex; align-items:center; justify-content:center; gap:8px; padding:14px 10px; background:${PANEL}; border:1px solid #2a2822; color:${BONE}; font-family:inherit; font-weight:800; font-size:15px; cursor:pointer; &:active{transform:translateY(1px)} &:disabled{opacity:.4; cursor:not-allowed}`;

/* day list */
const DayList = styled.div`display:flex; flex-direction:column; gap:10px; margin-top:18px;`;
const Empty = styled.div`text-align:center; color:${DIM}; font-weight:600; line-height:1.7; padding:34px 10px;`;
const DayCard = styled.div`background:${PANEL}; border:1px solid #2a2822; padding:12px 14px; cursor:pointer; &:active{border-color:${ACID}}`;
const DayHead = styled.div`display:flex; align-items:center; justify-content:space-between;`;
const DayDate = styled.div`display:flex; align-items:baseline; gap:8px;`;
const DayNum = styled.span`font-weight:900; font-size:24px; color:${BONE};`;
const DayWk = styled.span`font-weight:800; font-size:14px; color:${DIM};`;
const DayTotal = styled.span`font-weight:900; font-size:20px; color:${ACID}; font-variant-numeric:tabular-nums;`;
const DayEntries = styled.div`display:flex; flex-wrap:wrap; gap:6px; margin-top:10px;`;
const EntryPill = styled.span`display:inline-flex; align-items:center; gap:6px; background:#1d1b16; border:1px solid #2f2d26; padding:5px 10px; font-size:13px; font-weight:700;`;
const EntryTime = styled.span`color:${BONE}; font-variant-numeric:tabular-nums;`;
const EntryDur = styled.span`color:${ACID};`;
const EntryNote = styled.span`color:${DIM};`;
const Foot = styled.div`text-align:center; color:#4c4941; font-size:11px; font-weight:700; letter-spacing:1px; margin-top:30px;`;

/* overlay / sheet */
const Overlay = styled.div`position:fixed; inset:0; z-index:200; background:rgba(0,0,0,.6); display:flex; align-items:flex-end; justify-content:center; animation:${fade} .2s ease; @media(min-width:600px){align-items:center}`;
const Sheet = styled.div`width:100%; max-width:560px; max-height:92vh; overflow-y:auto; background:${INK}; border:1px solid #2a2822; padding:18px 18px calc(24px + env(safe-area-inset-bottom)); animation:${fade} .2s ease;`;
const SheetHead = styled.div`display:flex; align-items:center; justify-content:space-between; margin-bottom:16px;`;
const SheetTitle = styled.h2`margin:0; font-weight:900; font-size:22px; color:${BONE};`;
const FieldLbl = styled.div`font-weight:800; color:${DIM}; font-size:13px; letter-spacing:.5px; margin:16px 0 8px;`;
const DateInput = styled.input`width:100%; background:${PANEL}; border:1px solid #2a2822; color:${BONE}; font-family:inherit; font-size:16px; font-weight:700; padding:12px 14px; direction:ltr; text-align:right; color-scheme:dark;`;
const NoneYet = styled.div`color:${DIM}; font-weight:600; font-size:14px; padding:6px 0;`;
const EditRow = styled.div`display:flex; align-items:center; gap:10px; background:${(p) => (p.$active ? "#20220f" : PANEL)}; border:1px solid ${(p) => (p.$active ? ACID : "#2a2822")}; padding:11px 12px; margin-bottom:8px; cursor:pointer; transition:border-color .15s, background-color .15s; &:hover{border-color:${(p) => (p.$active ? ACID : "#4a4636")}}`;
const EditTimes = styled.span`font-weight:800; color:${BONE}; font-variant-numeric:tabular-nums;`;
const EditDur = styled.span`font-weight:800; color:${ACID}; font-size:14px;`;
const EditNote = styled.span`color:${DIM}; font-size:13px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;`;
const EditHint = styled.span`margin-inline-start:auto; color:#5a564d; font-size:11px; font-weight:700; flex:none;`;
const DelBtn = styled.button`display:grid; place-items:center; width:34px; height:34px; background:none; border:1px solid #3a2a2c; color:${RED}; cursor:pointer; flex:none; &:active{transform:translateY(1px)}`;
const AddGrid = styled.div`display:grid; grid-template-columns:1fr 1fr; gap:10px;`;
const TimeCol = styled.div`display:flex; flex-direction:column; gap:5px;`;
const TimeMini = styled.span`font-size:12px; font-weight:700; color:${DIM};`;
const T24Wrap = styled.div`display:flex; align-items:center; gap:6px; direction:ltr; background:${PANEL}; border:1px solid #2a2822; padding:8px 10px; justify-content:center;`;
const T24Sel = styled.select`background:#0e0d0a; color:${BONE}; border:1px solid #2a2822; font-family:inherit; font-size:22px; font-weight:800; padding:8px 6px; text-align:center; text-align-last:center; cursor:pointer; color-scheme:dark; &:focus{outline:none; border-color:${ACID}}`;
const T24Colon = styled.span`font-size:22px; font-weight:900; color:${BONE};`;
const NoteInput = styled.input`width:100%; margin-top:10px; background:${PANEL}; border:1px solid #2a2822; color:${BONE}; font-family:inherit; font-size:15px; font-weight:600; padding:12px 14px;`;
const AddBtn = styled.button`width:100%; margin-top:12px; display:flex; align-items:center; justify-content:center; gap:8px; background:${ACID}; color:${INK}; border:none; font-family:inherit; font-weight:900; font-size:17px; padding:14px; cursor:pointer; &:active{transform:translateY(1px)} &:disabled{opacity:.4; cursor:not-allowed}`;
const CancelBtn = styled.button`width:100%; margin-top:8px; background:none; border:1px solid #2a2822; color:${DIM}; font-family:inherit; font-weight:800; font-size:14px; padding:11px; cursor:pointer; &:active{transform:translateY(1px)}`;

/* report */
const RepBig = styled.div`display:flex; align-items:baseline; gap:10px; font-weight:900; font-size:46px; color:${ACID}; margin-bottom:14px;`;
const RepUnit = styled.span`font-size:14px; font-weight:700; color:${DIM};`;
const RepTable = styled.div`border:1px solid #2a2822;`;
const RepHeadRow = styled.div`display:grid; grid-template-columns:1.1fr 1.6fr .8fr; background:${PANEL}; border-bottom:1px solid #2a2822;`;
const RepTh = styled.div`padding:10px 12px; font-weight:800; color:${DIM}; font-size:13px;`;
const RepRow = styled.div`display:grid; grid-template-columns:1.1fr 1.6fr .8fr; border-bottom:1px solid #211f1a; &:last-child{border-bottom:none}`;
const RepTd = styled.div`padding:10px 12px; font-weight:${(p) => (p.$b ? 900 : 600)}; color:${(p) => (p.$b ? ACID : BONE)}; font-size:14px; ${(p) => (p.$span ? `grid-column: span ${p.$span};` : "")}`;
const SheetActions = styled.div`display:grid; grid-template-columns:1fr 1fr; gap:10px; margin-top:16px;`;
const PriBtn = styled.button`display:flex; align-items:center; justify-content:center; gap:8px; background:${BONE}; color:${INK}; border:none; font-family:inherit; font-weight:900; font-size:15px; padding:14px 10px; cursor:pointer; &:active{transform:translateY(1px)} &:disabled{opacity:.4}`;
const WhatsBtn = styled.button`display:flex; align-items:center; justify-content:center; gap:8px; background:#25D366; color:#062b12; border:none; font-family:inherit; font-weight:900; font-size:15px; padding:14px 10px; cursor:pointer; &:active{transform:translateY(1px)} &:disabled{opacity:.4}`;

/* all reports */
const MonthsList = styled.div`display:flex; flex-direction:column; gap:8px;`;
const MonthRow = styled.div`display:flex; align-items:center; gap:10px; background:${PANEL}; border:1px solid #2a2822; padding:6px 10px 6px 6px;`;
const MonthRowMain = styled.button`flex:1; display:flex; align-items:center; justify-content:space-between; gap:10px; background:none; border:none; color:${BONE}; font-family:inherit; cursor:pointer; padding:8px 6px; text-align:start;`;
const MonthRowName = styled.span`font-weight:800; font-size:16px;`;
const MonthRowVal = styled.span`font-weight:900; color:${ACID}; font-variant-numeric:tabular-nums; small{color:${DIM}; font-weight:700;}`;
const ConfirmDel = styled.div`display:flex; align-items:center; gap:6px;`;
const ConfirmTxt = styled.span`font-size:13px; font-weight:700; color:${DIM};`;
const ConfirmYes = styled.button`background:${RED}; color:#fff; border:none; font-family:inherit; font-weight:800; padding:7px 12px; cursor:pointer;`;
const ConfirmNo = styled.button`background:#2a2822; color:${BONE}; border:none; font-family:inherit; font-weight:800; padding:7px 12px; cursor:pointer;`;

/* print */
const PrintRoot = styled.div`position:fixed; inset:0; z-index:-1; background:#fff; color:#111; padding:32px; font-family:"Heebo","Assistant",system-ui,sans-serif;`;
const PDocHead = styled.div`border-bottom:3px solid #111; padding-bottom:12px; margin-bottom:18px;`;
const PDocTitle = styled.div`font-weight:900; font-size:26px;`;
const PDocName = styled.div`font-weight:700; font-size:16px; color:#444; margin-top:4px;`;
const PTable = styled.table`width:100%; border-collapse:collapse; font-size:14px;`;
const PTh = styled.th`text-align:right; padding:8px 10px; border-bottom:2px solid #111; font-weight:800;`;
const PTd = styled.td`text-align:right; padding:8px 10px; border-bottom:1px solid #ccc; font-weight:${(p) => (p.$b ? 800 : 500)};`;
const PSign = styled.div`margin-top:40px; font-weight:700; font-size:15px;`;

export default SigalHours;
